import { createSlice } from '@reduxjs/toolkit';

import { MAINTENANCE_MESSAGE, hideBranding, showFilenames, theme } from 'utils/globals';

import checkout from 'domains/checkout';
import photoModule from 'domains/photos';
import optionGroupModule from 'domains/option_groups';
import cartProductCreator from 'domains/cart_product_creator';
import cartProductEditor from 'domains/cart_product_editor';
import canvases from 'domains/canvases';
import promoCode from 'domains/promo_code';
import giftCert from 'domains/gift_cert';

import type { PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from 'store/wos_store';

export type SiteMessageState = {
  enabled?: boolean;
  message?: string;
  messageType?: 'notice' | 'error';
};

const validSidebarComponents = ['default', 'TextTools'] as const;
type SidebarComponentId = typeof validSidebarComponents[number];

const siteMessageInitialState: SiteMessageState = MAINTENANCE_MESSAGE.enabled
  ? {
      enabled: true,
      message: MAINTENANCE_MESSAGE.value,
      messageType: 'notice',
    }
  : {};

type UISliceInitialState = {
  visualOnlyRotation: 0 | 180;
  showSaveUncollectedPhotosDialog: boolean;
  showUploadPhotosDialog: boolean;
  hideBranding: boolean;
  showFilenames: boolean;
  hasMobileNavigation: boolean;
  showEditorSidebar: boolean; // always shown on desktop, default to false for mobile
  showMobileSidebar: boolean;
  showSidebarProducts: boolean;
  showMobileUserPhotos: boolean;
  showAdvancedFilters: boolean;
  siteMessage: SiteMessageState;
  theme: 'light' | 'dark';
  activeSpecialContentSidebarMenu: string | null;
  sidebarComponent: SidebarComponentId;
  errorMessages: {
    customSizePicker: string;
  };
};

const initialState: UISliceInitialState = {
  visualOnlyRotation: 0,
  activeSpecialContentSidebarMenu: null,
  showSaveUncollectedPhotosDialog: false,
  showUploadPhotosDialog: false,
  siteMessage: siteMessageInitialState,
  hideBranding,
  showFilenames,
  theme: theme && theme === 'light' ? 'light' : 'dark',
  hasMobileNavigation: false,
  showEditorSidebar: false, // always shown on desktop, default to false for mobile
  showMobileSidebar: false,
  showSidebarProducts: true,
  showMobileUserPhotos: true,
  showAdvancedFilters: false,
  sidebarComponent: 'default',
  errorMessages: {
    customSizePicker: '',
  },
};

const ui = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    rotateVisualOnly(state) {
      // At this time, we don't have products which can be multi-canvas _and_ visual only rotation
      // If we do add that, this will need to be linked to a canvas ID
      const update = { ...state };
      update.visualOnlyRotation = update.visualOnlyRotation === 180 ? 0 : 180;
      return update;
    },
    setActiveContentSidebarMenu(state, { payload }: PayloadAction<string | null>) {
      return { ...state, activeSpecialContentSidebarMenu: payload };
    },
    setShowUploadPhotosDialog(state, { payload }: PayloadAction<boolean>) {
      return { ...state, showUploadPhotosDialog: payload };
    },
    setSiteMessage(state, { payload }: PayloadAction<SiteMessageState>) {
      return { ...state, siteMessage: payload };
    },
    toggleHideBranding(state) {
      return { ...state, hideBranding: !state.hideBranding };
    },
    toggleShowFilenames(state) {
      return { ...state, showFilenames: !state.showFilenames };
    },
    toggleTheme(state) {
      const toggled = state.theme === 'light' ? 'dark' : 'light';
      return { ...state, theme: toggled };
    },
    setShowAdvancedFilters(state, { payload }: PayloadAction<boolean>) {
      return { ...state, showAdvancedFilters: payload };
    },
    setHasMobileNavigation(state, { payload }: PayloadAction<boolean>) {
      return { ...state, hasMobileNavigation: payload };
    },
    setShowEditorSidebar(state, { payload }: PayloadAction<boolean>) {
      return { ...state, showEditorSidebar: payload, activeSpecialContentSidebarMenu: null };
    },
    setShowMobileSidebar(state, { payload }: PayloadAction<boolean>) {
      return { ...state, showMobileSidebar: payload };
    },
    setShowSidebarProducts(state, { payload }: PayloadAction<boolean>) {
      return { ...state, showSidebarProducts: payload };
    },
    setShowMobileUserPhotos(state, { payload }: PayloadAction<boolean>) {
      return { ...state, showMobileUserPhotos: payload };
    },
    mobileNavigationProducts(state) {
      return { ...state, showSidebarProducts: true, showMobileSidebar: !state.showMobileSidebar };
    },
    mobileNavigationOptions(state) {
      return { ...state, showSidebarProducts: false, showMobileSidebar: !state.showMobileSidebar };
    },
    mobileNavigationPhotos(state) {
      return {
        ...state,
        showMobileSidebar: false,
        showMobileUserPhotos: !state.showMobileUserPhotos,
      };
    },
    mobileNavigationEdit(state) {
      return { ...state, showMobileUserPhotos: false, showMobileSidebar: false };
    },
    setSidebarComponent(state, { payload }: PayloadAction<SidebarComponentId>) {
      if (payload && validSidebarComponents.includes(payload)) {
        return { ...state, sidebarComponent: payload };
      }

      return { ...state, sidebarComponent: 'default' };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(cartProductCreator.actions.addToCart.rejected, generalServiceError)
      .addCase(promoCode.actions.save.rejected, generalServiceError)
      .addCase(giftCert.actions.save.rejected, generalServiceError)
      .addCase(photoModule.actions.createNewCollection, (state, { payload }) => {
        if (payload.action === 'create_from_upload') {
          return { ...state, showUploadPhotosDialog: true };
        }
      })
      .addCase(photoModule.actions.uploadPhotos.pending, (state) => {
        return {
          ...state,
          showUploadPhotosDialog: false,
        };
      })
      .addCase(cartProductEditor.actions.pending, (state) => {
        return {
          ...state,
          sidebarComponent: 'default',
          showAdvancedFilters: false,
        };
      })
      .addCase(optionGroupModule.actions.fetchOptionGroups.rejected, (state) => {
        return {
          ...state,
          siteMessage: {
            enabled: true,
            message: "We're experiencing some connection issues. Reloading the page may help.",
            messageType: 'notice',
          },
        };
      })
      .addCase('collections/updateUncollected/fulfilled', (state) => {
        return {
          ...state,
          showSaveUncollectedPhotosDialog: false,
        };
      })
      .addCase(canvases.actions.calculateCustomPrice.pending, clearCustomSizePickerError)
      .addCase(canvases.actions.changeActiveCanvasConfig.product, clearCustomSizePickerError)
      .addCase(canvases.actions.calculateCustomPrice.rejected, (state, { payload }) => {
        return {
          ...state,
          errorMessages: {
            ...state.errorMessages,
            customSizePicker: payload || 'Price Calculation Error',
          },
        };
      })
      .addCase(canvases.actions.calculateCustomOptionPrices.rejected, (state) => {
        return {
          ...state,
          errorMessages: {
            ...state.errorMessages,
            customSizePicker: 'Price Calculation Error',
          },
        };
      })
      .addCase(checkout.actions.submitOrder.rejected, (state, { payload }) => {
        // Existence of a payload indicates we're showing a local FlashMessage error, so skip the banner
        if (payload) {
          return state;
        }

        return {
          ...state,
          siteMessage: {
            message:
              'Order failed to submit. Try again or contact customer service at 1-800-435-6686',
            enabled: true,
            messageType: 'error',
          },
        };
      });
  },
});

function clearCustomSizePickerError(state: RootState['ui']) {
  return {
    ...state,
    errorMessages: {
      ...state.errorMessages,
      customSizePicker: '',
    },
  };
}

function generalServiceError(state: RootState['ui']) {
  const siteMessage: SiteMessageState = {
    enabled: true,
    message: "We're experiencing some service issues. Please try again later",
    messageType: 'error',
  };

  return { ...state, siteMessage };
}

export default ui;
